/**
 * <Contours> — the programme's chart motif (design system v3.2, 26.10.2026).
 *
 * Bathymetric contour lines drawn as nested, slightly irregular closed
 * curves around one or more "seamounts", with a few soundings set in the
 * display face. It replaces stock photography as the hero ground and sits
 * behind the dark bands and the footer, so the public pages share one
 * drawn device that is the programme's own: a depth chart, not a sea view.
 *
 * Everything is deterministic. The irregularity is a fixed sum of sines
 * per ring (no Math.random), so the server render, the client hydration
 * and the visual-regression baselines all see the same lines. The SVG is
 * pure stroke work on a transparent ground: the host band supplies the
 * colour, the component supplies only the line and its opacity.
 */
import type { CSSProperties } from 'react';

interface Seamount {
  /** Centre, in viewBox units (0–1 of width/height). */
  cx: number;
  cy: number;
  /** Innermost ring radius and ring spacing, in viewBox units of width. */
  r0: number;
  step: number;
  rings: number;
  /** Phase seed for the irregularity. */
  seed: number;
}

interface ContoursProps {
  /** Rendered viewBox; the SVG scales to its host with preserveAspectRatio slice. */
  width?: number;
  height?: number;
  /** Line colour; defaults to the on-deep chart line token. */
  stroke?: string;
  /** Overall opacity of the group. */
  opacity?: number;
  /** Which chart to draw: one broad plate (hero) or a tighter field (band, footer). */
  layout?: 'plate' | 'field' | 'corner';
  /** Draw depth soundings next to a few rings. */
  soundings?: boolean;
  style?: CSSProperties;
  className?: string;
}

const LAYOUTS: Record<NonNullable<ContoursProps['layout']>, Seamount[]> = {
  plate: [
    { cx: 0.78, cy: 0.42, r0: 0.03, step: 0.028, rings: 14, seed: 3 },
    { cx: 0.26, cy: 1.08, r0: 0.05, step: 0.034, rings: 9, seed: 11 },
  ],
  field: [
    { cx: 0.84, cy: 0.5, r0: 0.025, step: 0.03, rings: 10, seed: 5 },
    { cx: 0.12, cy: 0.2, r0: 0.02, step: 0.03, rings: 7, seed: 17 },
  ],
  corner: [{ cx: 1.02, cy: 0.1, r0: 0.03, step: 0.032, rings: 11, seed: 7 }],
};

/** Smooth, irregular closed ring: radius modulated by three fixed harmonics. */
function ringPath(sm: Seamount, i: number, W: number, H: number): string {
  const r = (sm.r0 + sm.step * i) * W;
  const n = 72;
  const pts: Array<[number, number]> = [];
  for (let k = 0; k < n; k++) {
    const t = (k / n) * Math.PI * 2;
    // Irregularity grows gently with the ring index so the outer lines wander
    // more than the inner ones, as real isobaths do.
    const wobble =
      1 +
      0.045 * Math.sin(3 * t + sm.seed * 0.7 + i * 0.21) +
      0.03 * Math.sin(5 * t - sm.seed * 1.3 + i * 0.13) +
      0.018 * Math.sin(8 * t + sm.seed * 2.1 - i * 0.07);
    const rr = r * (wobble + i * 0.004);
    pts.push([sm.cx * W + rr * Math.cos(t), sm.cy * H + rr * Math.sin(t) * 0.86]);
  }
  // Catmull-Rom → cubic Bézier, closed.
  let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let k = 0; k < n; k++) {
    const p0 = pts[(k - 1 + n) % n];
    const p1 = pts[k];
    const p2 = pts[(k + 1) % n];
    const p3 = pts[(k + 2) % n];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return d + ' Z';
}

export interface ContourGeometry {
  paths: Array<{ d: string; w: number }>;
  labels: Array<{ x: number; y: number; v: number }>;
}

/**
 * The chart's geometry as data — shared with the Open Graph renderer, which
 * cannot use CSS variables or React class names but can draw SVG paths.
 */
export function contourGeometry(layout: NonNullable<ContoursProps['layout']>, width: number, height: number, soundings = true): ContourGeometry {
  const mounts = LAYOUTS[layout];
  const paths: ContourGeometry['paths'] = [];
  const labels: ContourGeometry['labels'] = [];
  mounts.forEach((sm, mi) => {
    for (let i = 0; i < sm.rings; i++) {
      // Every fourth isobath is an index contour: drawn a hair heavier, as on
      // a chart, and the one that carries a sounding.
      const index = i % 4 === 0;
      paths.push({ d: ringPath(sm, i, width, height), w: index ? 1.1 : 0.7 });
      if (soundings && index && i > 0 && mi === 0) {
        const r = (sm.r0 + sm.step * i) * width;
        const t = -0.62; // upper-right of the mount, where the lines are open
        labels.push({ x: sm.cx * width + r * Math.cos(t) * 1.02, y: sm.cy * height + r * Math.sin(t) * 0.86 - 4, v: i * 6 });
      }
    }
  });
  return { paths, labels };
}

export function Contours({
  width = 1440,
  height = 760,
  stroke = 'var(--color-chart-line)',
  opacity = 1,
  layout = 'plate',
  soundings = true,
  style,
  className,
}: ContoursProps) {
  const { paths, labels } = contourGeometry(layout, width, height, soundings);
  return (
    <svg
      className={className ? `ind-chart-bg ${className}` : 'ind-chart-bg'}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
      style={style}
    >
      <g fill="none" stroke={stroke} opacity={opacity} strokeLinejoin="round">
        {paths.map((p, i) => (
          <path key={i} d={p.d} strokeWidth={p.w} />
        ))}
      </g>
      {soundings && (
        <g fill={stroke} opacity={opacity} fontFamily="var(--font-heading)" fontWeight={600} fontSize={13} letterSpacing="0.08em">
          {labels.map((l, i) => (
            <text key={i} x={l.x.toFixed(1)} y={l.y.toFixed(1)}>
              {l.v}
            </text>
          ))}
        </g>
      )}
    </svg>
  );
}
